import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  TrendingUp, TrendingDown, Activity, DollarSign, Shield,
  RefreshCw, Zap, ArrowRight, Loader2, Pickaxe, Radio,
  ChevronRight, Cpu, Search,
} from "lucide-react";
import { toast } from "sonner";

import { API } from '../config';

const Dashboard = () => {
  const { token } = useAuth();
  const [bots, setBots] = useState([]);
  const [prices, setPrices] = useState({});
  const [minerStatus, setMinerStatus] = useState(null);
  const [ufStatus, setUfStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchAll();
    const i1 = setInterval(fetchBots, 15000);
    const i2 = setInterval(fetchPrices, 20000);
    return () => { clearInterval(i1); clearInterval(i2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchBots(), fetchPrices(), fetchMiner(), fetchUF()]);
    } catch (e) {
      console.error("Dashboard fetch error:", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchBots = async () => {
    try {
      const res = await axios.get(`${API}/eu-bot/dca/all-bots`, { headers });
      const d = res.data;
      setBots(Array.isArray(d) ? d : d.bots || []);
    } catch (e) { console.error("fetchBots", e); }
  };

  const fetchPrices = async () => {
    try {
      const res = await axios.get(`${API}/market/prices`);
      setPrices(res.data);
    } catch (e) { console.error("fetchPrices", e); }
  };

  const fetchMiner = async () => {
    try {
      const res = await axios.get(`${API}/eu-bot/titan-hunt`, { headers });
      setMinerStatus(res.data);
    } catch (e) { console.error("fetchMiner", e); }
  };

  const fetchUF = async () => {
    try {
      const res = await axios.get(`${API}/eu-bot/ultimate-fighter/status`, { headers });
      setUfStatus(res.data);
    } catch (e) { console.error("fetchUF", e); }
  };

  // Compute fleet totals
  const onlineBots = bots.filter(b => b.connected);
  const totalEquity = bots.reduce((s, b) => s + (b.equity || 0), 0);
  const totalCapital = bots.reduce((s, b) => s + (b.initial_capital || 0), 0);
  const totalPnl = bots.reduce((s, b) => {
    const realized = b.total_profit || 0;
    const unrealized = b.in_deal && b.avg_entry_price > 0
      ? ((b.current_price || 0) - b.avg_entry_price) * (b.total_qty || 0) : 0;
    return s + realized + unrealized;
  }, 0);
  const totalDeals = bots.reduce((s, b) => s + (b.deals_completed || 0), 0);

  // Miner info
  const miner = minerStatus?.miner || {};
  const titans = minerStatus?.titans || [];
  const astronauts = minerStatus?.astronauts || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-5 h-5 text-[#00d4ff] animate-spin mr-2" />
        <span className="text-zinc-500 text-sm" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Loading SKYNET...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      <style>{`
        .glow-cyan { text-shadow: 0 0 20px rgba(0,212,255,0.3); }
        .glow-green { text-shadow: 0 0 15px rgba(0,255,157,0.3); }
        .glow-red { text-shadow: 0 0 15px rgba(255,0,85,0.3); }
        .glass { backdrop-filter: blur(16px); background: rgba(24,24,27,0.5); border: 1px solid rgba(255,255,255,0.06); }
        @keyframes pulse-slow { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
        .pulse-slow { animation: pulse-slow 2s ease-in-out infinite; }
        .nav-card { transition: all 0.2s; }
        .nav-card:hover { transform: translateY(-2px); border-color: rgba(255,255,255,0.12); }
      `}</style>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-white" style={{fontFamily:'Manrope,sans-serif'}}>
            SKYNET <span style={{color:'#00d4ff'}}>TRADER</span>
          </h1>
          <p className="text-[11px] text-zinc-600 mt-0.5" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>
            Live trading command center
          </p>
        </div>
        <button onClick={fetchAll} className="p-2 rounded-lg border border-zinc-800 text-zinc-500 hover:text-zinc-300 hover:border-zinc-600 transition-all" data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Hero: Fleet Overview */}
      <div className="glass rounded-2xl p-6" data-testid="fleet-hero">
        <div className="flex items-center gap-2 mb-4">
          <div className={`w-2.5 h-2.5 rounded-full ${onlineBots.length > 0 ? 'pulse-slow' : ''}`} style={{backgroundColor: onlineBots.length > 0 ? '#00ff9d' : '#52525b'}} />
          <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold" style={{fontFamily:'Manrope,sans-serif'}}>
            Fleet Status
          </span>
          <span className="text-[10px] font-mono text-zinc-600 ml-auto">{onlineBots.length}/{bots.length} online</span>
        </div>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 mb-1" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Total Equity</div>
            <div className="text-4xl md:text-5xl font-extrabold text-white glow-cyan" style={{fontFamily:"'JetBrains Mono',monospace"}} data-testid="total-equity">
              ${totalEquity.toFixed(0)}
            </div>
            <div className="inline-flex items-center gap-2 mt-2 px-3 py-1.5 rounded-full" style={{background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)'}}>
              {totalPnl >= 0 ? <TrendingUp className="w-3.5 h-3.5" style={{color:'#00ff9d'}} /> : <TrendingDown className="w-3.5 h-3.5" style={{color:'#ff0055'}} />}
              <span className="text-base font-bold font-mono" style={{color: totalPnl >= 0 ? '#00ff9d' : '#ff0055'}} data-testid="total-pnl">
                {totalPnl >= 0 ? "+" : ""}{totalPnl.toFixed(2)}
              </span>
              <span className="text-[10px] text-zinc-600 font-mono">USD</span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-6">
            <div className="text-center md:text-right">
              <div className="text-[9px] text-zinc-600 uppercase tracking-widest" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Capital</div>
              <div className="text-lg font-bold font-mono text-zinc-300">${totalCapital.toFixed(0)}</div>
            </div>
            <div className="text-center md:text-right">
              <div className="text-[9px] text-zinc-600 uppercase tracking-widest" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Est/Day</div>
              <div className="text-lg font-bold font-mono" style={{color:'#00d4ff'}}>
                ${(totalEquity * 0.51 / 365).toFixed(0)}
              </div>
            </div>
            <div className="text-center md:text-right">
              <div className="text-[9px] text-zinc-600 uppercase tracking-widest" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Deals</div>
              <div className="text-lg font-bold font-mono text-zinc-300">{totalDeals}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bot Cards */}
      {bots.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {bots.map(bot => {
            const entry = bot.avg_entry_price || 0;
            const curr = bot.current_price || 0;
            const pctPnl = entry > 0 ? ((curr - entry) / entry * 100) : 0;
            const tp = bot.take_profit_pct || 1;
            const tpPrice = entry > 0 ? entry * (1 + tp / 100) : 0;
            const prog = Math.max(0, Math.min((pctPnl / tp) * 100, 100));
            const pnlColor = pctPnl >= 0 ? '#00ff9d' : '#ff0055';
            const gaugeStroke = prog >= 60 ? '#00ff9d' : pctPnl < 0 ? '#ff0055' : '#00d4ff';
            const coin = bot.symbol?.replace('USDC','').replace('USDT','') || '?';
            const r = 32;
            const circ = 2 * Math.PI * r;
            const offset = circ - (prog / 100) * circ;

            return (
              <div key={bot.bot_id} className="glass rounded-xl p-5 nav-card cursor-pointer" onClick={() => navigate('/eu-bot')} data-testid={`bot-card-${bot.bot_id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-black border-2" style={{borderColor: bot.connected ? '#00ff9d' : '#52525b', color: bot.connected ? '#fafafa' : '#52525b', background: bot.connected ? 'rgba(0,255,157,0.08)' : 'transparent'}}>
                      {coin}
                    </div>
                    <div>
                      <div className="text-sm font-extrabold text-white uppercase" style={{fontFamily:'Manrope,sans-serif'}}>{bot.symbol?.replace('USDC','/USDC').replace('USDT','/USDT') || '?'}</div>
                      <div className="text-[9px] text-zinc-600" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>{bot.bot_id?.replace('-',' ')}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full" style={{color: bot.status === 'IN_DEAL' ? '#ffbe0b' : '#00ff9d', background: bot.status === 'IN_DEAL' ? 'rgba(255,190,11,0.08)' : 'rgba(0,255,157,0.08)', border: `1px solid ${bot.status === 'IN_DEAL' ? 'rgba(255,190,11,0.2)' : 'rgba(0,255,157,0.2)'}`}}>
                      {bot.status}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xl font-extrabold font-mono" style={{color: pnlColor}}>
                      {pctPnl >= 0 ? "+" : ""}{pctPnl.toFixed(2)}%
                    </div>
                    <div className="text-[10px] text-zinc-600 font-mono">${(bot.equity || 0).toFixed(0)} equity</div>
                  </div>
                  <div className="relative" style={{width: 70, height: 70}}>
                    <svg width="70" height="70" className="transform -rotate-90">
                      <circle cx="35" cy="35" r={r} fill="none" stroke="#27272a" strokeWidth="4" />
                      <circle cx="35" cy="35" r={r} fill="none" stroke={gaugeStroke} strokeWidth="4"
                        strokeDasharray={circ} strokeDashoffset={offset}
                        strokeLinecap="round" className="transition-all duration-1000"
                        style={{filter: `drop-shadow(0 0 4px ${gaugeStroke}60)`}} />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-xs font-bold font-mono text-white">{prog.toFixed(0)}%</span>
                      <span className="text-[7px] text-zinc-600">to TP</span>
                    </div>
                  </div>
                </div>
                {entry > 0 && (
                  <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-white/5">
                    <div>
                      <div className="text-[8px] text-zinc-600 uppercase" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>TP Target</div>
                      <div className="text-xs font-bold font-mono" style={{color:'#00ff9d'}}>${tpPrice < 1 ? tpPrice.toFixed(4) : tpPrice.toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="text-[8px] text-zinc-600 uppercase" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Entry</div>
                      <div className="text-xs font-bold font-mono text-zinc-400">${entry < 1 ? entry.toFixed(4) : entry.toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="text-[8px] text-zinc-600 uppercase" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>SO</div>
                      <div className="text-xs font-bold font-mono text-zinc-400">{bot.safety_orders_filled || 0}/{bot.max_safety_orders || 15}</div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* HIGHLANDER Miner + Live Market */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Miner Status */}
        <div className="glass rounded-xl p-5" data-testid="miner-status">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4" style={{color:'#ffbe0b'}} />
              <span className="text-sm font-bold text-white" style={{fontFamily:'Manrope,sans-serif'}}>HIGHLANDER Miner</span>
            </div>
            <div className={`w-2 h-2 rounded-full ${miner.running ? 'pulse-slow' : ''}`} style={{backgroundColor: miner.running ? '#00ff9d' : '#52525b'}} />
          </div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div>
              <div className="text-[8px] text-zinc-600 uppercase tracking-widest" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Cycles</div>
              <div className="text-base font-bold font-mono text-zinc-300">{(miner.cycles || 0).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-[8px] text-zinc-600 uppercase tracking-widest" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Discoveries</div>
              <div className="text-base font-bold font-mono" style={{color:'#ffbe0b'}}>{miner.discoveries || 0}</div>
            </div>
            <div>
              <div className="text-[8px] text-zinc-600 uppercase tracking-widest" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Titans</div>
              <div className="text-base font-bold font-mono" style={{color:'#00ff9d'}}>{titans.length}</div>
            </div>
          </div>
          {ufStatus?.phase && ufStatus.phase !== "idle" && (
            <div className="mt-3 pt-3 border-t border-white/5">
              <div className="flex items-center gap-2 mb-1">
                <Cpu className="w-3.5 h-3.5" style={{color:'#00d4ff'}} />
                <span className="text-[10px] uppercase tracking-wider text-zinc-500" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Ultimate Fighter</span>
              </div>
              <div className="text-xs font-mono text-zinc-400">{ufStatus.phase}</div>
              {ufStatus.total > 0 && (
                <div className="mt-1.5 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{width: `${Math.max((ufStatus.progress / ufStatus.total) * 100, 1)}%`, backgroundColor: '#00d4ff'}} />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Live Market */}
        <div className="glass rounded-xl p-5" data-testid="live-market">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-4 h-4 text-zinc-500" />
            <span className="text-sm font-bold text-white" style={{fontFamily:'Manrope,sans-serif'}}>Live Market</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {["BTCUSDT", "ETHUSDT", "SOLUSDT", "BNBUSDT"].map(sym => {
              const price = prices[sym];
              return (
                <div key={sym} className="bg-white/[0.03] rounded-lg p-3" data-testid={`price-${sym}`}>
                  <div className="text-[9px] text-zinc-600 uppercase" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>{sym.replace("USDT", "/USDT")}</div>
                  <div className="font-mono text-sm font-bold text-zinc-200">
                    ${price ? Number(price).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : "---"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Quick Navigation */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <button onClick={() => navigate("/eu-bot")} className="glass rounded-xl p-4 text-left nav-card" data-testid="nav-fleet">
          <div className="flex items-center gap-2 mb-1.5">
            <Radio className="w-4 h-4" style={{color:'#00ff9d'}} />
            <span className="text-sm font-bold text-white" style={{fontFamily:'Manrope,sans-serif'}}>Fleet Command</span>
            <ChevronRight className="w-3.5 h-3.5 text-zinc-700 ml-auto" />
          </div>
          <p className="text-[10px] text-zinc-600" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Live bot monitoring, controls, deploy</p>
        </button>
        <button onClick={() => navigate("/mining")} className="glass rounded-xl p-4 text-left nav-card" data-testid="nav-mining">
          <div className="flex items-center gap-2 mb-1.5">
            <Pickaxe className="w-4 h-4" style={{color:'#ffbe0b'}} />
            <span className="text-sm font-bold text-white" style={{fontFamily:'Manrope,sans-serif'}}>Strategy Mining</span>
            {miner.running && <span className="text-[8px] font-mono px-1.5 py-0.5 rounded-full ml-auto" style={{color:'#ffbe0b', background:'rgba(255,190,11,0.1)', border:'1px solid rgba(255,190,11,0.2)'}}>ACTIVE</span>}
            <ChevronRight className="w-3.5 h-3.5 text-zinc-700 ml-auto" />
          </div>
          <p className="text-[10px] text-zinc-600" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>HIGHLANDER engine, strategy discovery</p>
        </button>
        <button onClick={() => navigate("/registry")} className="glass rounded-xl p-4 text-left nav-card" data-testid="nav-registry">
          <div className="flex items-center gap-2 mb-1.5">
            <Shield className="w-4 h-4" style={{color:'#00d4ff'}} />
            <span className="text-sm font-bold text-white" style={{fontFamily:'Manrope,sans-serif'}}>Strategy Registry</span>
            <ChevronRight className="w-3.5 h-3.5 text-zinc-700 ml-auto" />
          </div>
          <p className="text-[10px] text-zinc-600" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>5,213 strategies evaluated</p>
        </button>
      </div>
    </div>
  );
};

export default Dashboard;
